/**
 * Текст основной записи (§6.2).
 *
 * Версионируется, потому что будет меняться: финальный вариант должен быть
 * отдельно протестирован по длительности, разнообразию фонем, эмоциональным
 * переходам и пригодности для обоих провайдеров (§6.2, последний абзац).
 * Без версии непонятно, на каком тексте обучен конкретный аватар.
 *
 * Требование к тексту: связный мини-монолог с естественными эмоциональными
 * переходами, а НЕ набор команд «скажите радостно / скажите грустно».
 */

export interface OnboardingScriptBlock {
  /** Подсказка о подаче. Показывается рядом, но не читается вслух. */
  hint: string;
  text: string;
}

export interface OnboardingScript {
  version: string;
  language: string;
  estimatedSec: number;
  blocks: OnboardingScriptBlock[];
}

export const onboardingScript: OnboardingScript = {
  // 1.1: текст сокращён под новую длительность записи (§6.3) — минуты чтения
  // вслух ради одного кадра и минуты звука человек тратил зря.
  version: '1.1',
  language: 'ru',
  estimatedSec: 80,
  blocks: [
    {
      hint: 'Спокойно, как обычно',
      text: 'Здравствуйте! Это моя тестовая запись.',
    },
    {
      hint: 'Ровно и размеренно',
      text:
        'Обычно я говорю спокойно и достаточно размеренно. Мне важно, чтобы мой цифровой голос ' +
        'звучал естественно и был похож на меня.',
    },
    {
      hint: 'С энергией',
      text:
        'Но иногда есть вещи, которыми действительно хочется поделиться с энергией. Представьте: ' +
        'мы только что узнали отличную новость — проект запустился, и первые результаты оказались ' +
        'даже лучше, чем мы ожидали!',
    },
    {
      hint: 'Вопросительно',
      text:
        'А теперь представим другую ситуацию. Что, если всё пойдёт совсем не по плану? ' +
        'Что мы будем делать дальше?',
    },
    {
      hint: 'Серьёзно и уверенно',
      text:
        'Я считаю, что даже в сложной ситуации важно говорить спокойно, чётко и уверенно. ' +
        'Всегда можно остановиться, посмотреть на факты и найти решение.',
    },
    {
      hint: 'С улыбкой',
      text: 'А иногда хочется просто улыбнуться и сказать: это было действительно здорово.',
    },
  ],
};

export const onboardingScriptText = (script = onboardingScript): string =>
  script.blocks.map((block) => block.text).join('\n\n');

/**
 * Доля каждого блока в общей длительности записи.
 *
 * Раньше суфлёр делил время поровну: шесть блоков — по шестой части каждому.
 * Блоки при этом разной длины, и на короткой фразе человек несколько секунд
 * сидел молча и ждал, пока текст поедет дальше. Это видно на записи: паузы
 * не там, где нужны по смыслу, а там, где кончился текст блока.
 *
 * Считаем по длине текста. Минимальная доля есть у каждого блока: самая
 * короткая фраза всё равно требует времени на вдох и произнесение.
 */
const MIN_BLOCK_SHARE = 0.06;

export function scriptBlockShares(script: OnboardingScript): number[] {
  const lengths = script.blocks.map((block) => Math.max(1, block.text.length));
  const total = lengths.reduce((sum, value) => sum + value, 0);

  const raw = lengths.map((length) => Math.max(MIN_BLOCK_SHARE, length / total));
  const rawTotal = raw.reduce((sum, value) => sum + value, 0);

  // После подъёма коротких блоков сумма перестаёт быть единицей — нормируем.
  return raw.map((share) => share / rawTotal);
}

/** Какой блок читается при заданной доле прочитанного (0-1). */
export function scriptBlockAt(script: OnboardingScript, progress: number): number {
  const shares = scriptBlockShares(script);
  let passed = 0;

  for (const [index, share] of shares.entries()) {
    passed += share;
    if (progress < passed) return index;
  }

  return script.blocks.length - 1;
}
